import React, { useCallback, useMemo, useState } from 'react';

import { Select } from '@aline/common';
import { link } from '@aline/routes';
import { Typography } from '@mui/material';
import type {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel,
  MuiEvent,
} from '@mui/x-data-grid-premium';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  allOwnerFilters,
  FiltersToolbarButton,
  getIsOwnerCellValue,
  getStringOperatorsWithSafeIsAnyOf,
  ListCountFooter,
  ListEmpty,
  OwnerCell,
  ReportFiltersPanel,
  SearchInput,
  Spacer,
  useListToolbarStyles,
} from 'src/components';
import { GridAlineTooltip } from 'src/components/generic/data-grid/grid-aline-tooltip';
import type { Apollo } from 'src/gql/generated';
import {
  formatDateForDisplay,
  getIsBrowserHandledLinkClick,
  openLink,
  openLinkFromAnchorClick,
} from 'src/helpers';
import {
  createDataGridPremiumPaperStyles,
  FiltersAnchorProvider,
  useDataGridSearch,
  useFiltersAnchorState,
  useGetPlaybooks,
} from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { NewPlaybookButton } from './NewPlaybookButton';
import { usePlaybooksListLiveUpdates } from './use-playbooks-live-updates';

const minWidthNameCellPx = 350;

type PlaybookRow = Apollo.GetPlaybooksQuery['playbooks'][number];
type PlaybookColDef = GridColDef<PlaybookRow>;

const useStyles = makeStyles()(theme => ({
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    position: 'relative',
  },
  nameLink: {
    color: theme.appColors.iconsType,
    minWidth: 0,
    textDecoration: 'none',
  },
  xGrid: {
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
    flexGrow: 1,
    minHeight: 0,
  },
}));

export function PlaybooksTab(): JSX.Element {
  const { classes } = useStyles();
  const { anchorEl: filtersAnchorEl, setAnchorEl: setFiltersAnchorEl } = useFiltersAnchorState();
  const { loading, playbooks, refetch } = useGetPlaybooks({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  usePlaybooksListLiveUpdates({ refetch });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'createdAt' satisfies keyof PlaybookRow, sort: 'desc' },
  ]);
  const columnDefs = useMemo((): PlaybookColDef[] => {
    return [
      {
        field: 'title',
        filterOperators: getStringOperatorsWithSafeIsAnyOf(),
        filterable: true,
        flex: 2.5,
        headerName: 'Name',
        minWidth: minWidthNameCellPx,
        renderCell: NameCell,
        type: 'string',
        // Filter/sort/quick-search on the same name the cell shows, so an
        // untitled playbook is matched by searching "Untitled playbook".
        valueGetter: (_value, row) => getPlaybookName(row.title),
      },
      {
        field: 'createdBy',
        filterOperators: allOwnerFilters,
        filterable: true,
        flex: 1,
        getApplyQuickFilterFn: (value: string) => {
          return cellValue => {
            return (
              getIsOwnerCellValue(cellValue) &&
              typeof cellValue.displayName === 'string' &&
              cellValue.displayName.toLowerCase().includes(value.toLowerCase())
            );
          };
        },
        headerName: 'Author',
        minWidth: 180,
        renderCell: AuthorCell,
        sortComparator: (v1, v2) => {
          const v1Name = getIsOwnerCellValue(v1) ? (v1.displayName ?? '') : '';
          const v2Name = getIsOwnerCellValue(v2) ? (v2.displayName ?? '') : '';
          return v1Name.localeCompare(v2Name);
        },
        type: 'string',
      },
      {
        field: 'ruleCount',
        flex: 1,
        headerName: 'Rules',
        type: 'number',
      },
      {
        field: 'usedInDocumentCount',
        flex: 1,
        headerName: 'Used in documents',
        type: 'number',
        valueGetter: (_value, row) => row.usedInDocumentCount,
      },
      {
        field: 'createdAt',
        flex: 1,
        headerName: 'Date created',
        valueFormatter: value => formatDateForDisplay(value),
      },
    ];
  }, []);

  const handleRowClick = useCallback(
    ({ row }: GridRowParams<PlaybookRow>, event: MuiEvent<React.MouseEvent<HTMLElement>>) => {
      /**
       * A modifier or middle click is a request for a new browser tab. The row background is
       * not a link, so honouring it here would navigate the current tab instead. Do nothing
       * and let the row's name link be the thing that opens in a new tab.
       */
      if (getIsBrowserHandledLinkClick(event)) return;

      openLink(link.PlaybooksDetail({ playbookId: row.id }), {
        title: getPlaybookName(row.title),
      });
    },
    []
  );

  return (
    <FiltersAnchorProvider setAnchorEl={setFiltersAnchorEl}>
      <Helmet>
        <title>Playbooks</title>
      </Helmet>
      <div className={classes.listContainer}>
        <DataGridPremium
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          showToolbar
          className={classes.xGrid}
          columns={columnDefs}
          loading={loading}
          rows={playbooks}
          slotProps={{
            filterPanel: {
              filterableColumns: 'visible',
            },
            footer: {
              noun: 'playbooks',
              totalCount: playbooks.length,
            },
            loadingOverlay: {
              noRowsVariant: 'linear-progress',
              variant: 'linear-progress',
            },
            panel: {
              placement: 'bottom-start',
              target: filtersAnchorEl ?? undefined,
            },
          }}
          slots={{
            baseTooltip: GridAlineTooltip,
            filterPanel: ReportFiltersPanel,
            footer: ListCountFooter,
            noRowsOverlay: PlaybooksListEmpty,
            toolbar: PlaybooksListToolbar,
          }}
          sortModel={sortModel}
          sx={createDataGridPremiumPaperStyles}
          onRowClick={handleRowClick}
          onSortModelChange={setSortModel}
        />
      </div>
    </FiltersAnchorProvider>
  );
}

function getPlaybookName(title: string): string {
  return title || 'Untitled playbook';
}

function NameCell({ row }: GridRenderCellParams<PlaybookRow>): JSX.Element {
  const { classes } = useStyles();
  const name = getPlaybookName(row.title);
  const href = link.PlaybooksDetail({ playbookId: row.id });
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) =>
      openLinkFromAnchorClick(event, href, { title: name }),
    [href, name]
  );

  return (
    <Link className={classes.nameLink} to={href} onClick={handleClick}>
      <Typography noWrap data-cy={Select.playbooks.nameCell}>
        {name}
      </Typography>
    </Link>
  );
}

function AuthorCell({ row }: GridRenderCellParams<PlaybookRow>): JSX.Element {
  if (!row.createdBy) return <Typography>No author</Typography>;
  return <OwnerCell showName row={{ owner: row.createdBy }} />;
}

function PlaybooksListEmpty(): JSX.Element {
  return <ListEmpty subtitle="Create your first playbook to get started." title="No playbooks" />;
}

function PlaybooksListToolbar(): JSX.Element {
  const { classes } = useListToolbarStyles();
  const { search, setSearch } = useDataGridSearch();

  return (
    <div className={classes.toolbar}>
      <FiltersToolbarButton />
      <Spacer />
      <SearchInput placeholder="Search playbooks..." search={search} setSearch={setSearch} />
      <NewPlaybookButton />
    </div>
  );
}
